import { useCallback, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { useToast } from '@winnin/winnin_ui.components.toast'

import LinkToast from 'components/Toasts/LinkToast'
import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { useSelectedItemsList } from 'contexts/SelectedItemsListProvider'
import { useWorkspaceCompanyPlan } from 'hooks/useWorkspaceCompanyPlan'
import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { jobUtils } from 'utils/jobUtils'

import AddToJobFolderModalContent from './AddToJobFolderModalContent'
import { GET_WORKSPACE_COMPANY_PLAN_LOG_ERROR, MODAL_STYLE } from './const'
import { useJobsWithFolders } from './hooks/useJobsWithFolders'
import { SAVE_PINS_TO_JOB_FOLDERS } from './query'
import { trackAddedItems } from './utils/trackAddedItems'

const TOLGEE_PREFIX = 'AddToJobFolderModal'
const alreadyAddedToastKey = `${TOLGEE_PREFIX}-alreadyAddedToast`
const editJobToastLinkKey = `${TOLGEE_PREFIX}-editJobToast-link`
const limitReachedToastKey = `${TOLGEE_PREFIX}-limitReachedToast`

export default function AddToJobFolderModal({ isOpen, setIsOpen }) {
  const workspaceId = PanelStore?.selectedPanel?.id

  const { setIsCreateDashboardModalOpen } = useDashboardModals()
  const { selectedItemsBar, setSelectedItemsBar } = useSelectedItemsList()
  const { createToast, deleteToastById } = useToast()
  const t = useTranslate()

  const { workspaceCompanyPlan, getWorkspaceCompanyPlanFromApi } = useWorkspaceCompanyPlan({
    errorLog: GET_WORKSPACE_COMPANY_PLAN_LOG_ERROR,
  })

  useEffect(() => {
    getWorkspaceCompanyPlanFromApi()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    jobsList,
    mostRecentJob,
    isFetchingMore,
    isLoading,
    jobsRows,
    searchTerm,
    handleSearchTermChange,
    resetJobsPagination,
    loadMoreJobs,
    hasMoreJobs,
  } = useJobsWithFolders({ workspaceId })

  const getTolgeeKey = useCallback(key => `${key}-asDashboard`, [])

  const handleMutationCompleted = useCallback(
    (data, clientOptions) => {
      if (data?.createWorkspaceJobPins?.success) {
        const savedInput = clientOptions?.variables?.createWorkspaceJobPinInput?.[0]
        const savedJobId = clientOptions?.variables?.workspaceJobId
        const savedFolderId = savedInput?.folders?.[0]

        if (savedJobId) {
          const jobFromList = jobsList.find(job => job.id === savedJobId)
          const folderFromList = jobFromList?.folders?.find(folder => folder.id === savedFolderId)
          const jobName = jobFromList?.name ?? ''
          const folderName = folderFromList?.name ?? 'Todas as referências'
          const folderId = folderFromList?.id ?? 'allReferences'

          const infoToast = {
            message: t({
              key: `${TOLGEE_PREFIX}-successToast`,
              parameters: {
                h: <span className="highlightText" />,
                folderName,
                jobName,
              },
              defaultValue: 'Suas referências foram adicionadas à pasta <h>{folderName}</h> em <h>{jobName}</h>', // TODO: tolgee plural
            }),
            link: `/workspace/${workspaceId}/jobs/${jobFromList.id}/folder/${folderId}/all`,
          }

          createToast({
            content: <LinkToast toast={infoToast} inline />,
          })
        }
      } else {
        if (data?.createWorkspaceJobPinInput?.message === 'limit-reached') {
          return createToast({
            content: t({
              key: getTolgeeKey(limitReachedToastKey),
              parameters: {
                b: <strong></strong>,
                p: <p></p>,
              },
              defaultValue:
                '<p>Seu dashboard está <b>cheio</b>, para salvar com sucesso novas referências edite seu dashboard ou crie um novo.</p>',
            }),
          })
        }

        createToast({
          content: t({
            key: getTolgeeKey(alreadyAddedToastKey),
            defaultValue: 'You already added this content to this dashboard',
          }),
        })
      }
    },
    [createToast, getTolgeeKey, jobsList, t, workspaceId]
  )

  const [savePinsToJobFolders] = useMutation(SAVE_PINS_TO_JOB_FOLDERS, {
    onError: error => {
      console.error('error @CREATE_JOB_FOLDER', error)

      if (String(error?.message).toLowerCase() === 'limit-reached') {
        return createToast({
          content: t({
            key: getTolgeeKey(limitReachedToastKey),
            parameters: {
              b: <strong></strong>,
              p: <p></p>,
            },
            defaultValue:
              '<p>Seu dashboard está <b>cheio</b>, para salvar com sucesso novas referências edite seu dashboard ou crie um novo.</p>',
          }),
        })
      }
    },
  })

  const handleClose = useCallback(() => {
    setIsOpen(false)
    resetJobsPagination()
  }, [setIsOpen, resetJobsPagination])

  const handleOpenDashboardCreationModal = () => {
    setIsCreateDashboardModalOpen(true)
  }

  const formatDataForCacheUpdate = (newData = []) => {
    const separatedPinCount = newData.reduce(
      (prev, curr) => {
        const dataWithTotal = {
          ...prev,
          totalPins: prev?.totalPins + 1,
        }
        if (curr?.type === 'creators') {
          return {
            ...dataWithTotal,
            creatorAmount: prev?.creatorAmount + 1,
          }
        }
        if (curr?.type === 'posts') {
          return {
            ...dataWithTotal,
            postAmount: prev?.postAmount + 1,
          }
        }
        return {
          ...dataWithTotal,
          territoriesAmount: prev?.territoriesAmount + 1,
        }
      },
      {
        creatorAmount: 0,
        postAmount: 0,
        territoriesAmount: 0,
        totalPins: 0,
      }
    )
    return separatedPinCount
  }

  const handleSavePinsToJobFolders = useCallback(
    async (jobId, foldersIds) => {
      const selectedJob = jobsList.find(job => job.id === jobId)
      const amountByTypes = jobUtils.getPinsAmountByTypes(selectedJob)
      const newTotal = amountByTypes?.totalAmount + selectedItemsBar?.length
      const limit = workspaceCompanyPlan?.jobPins

      const itemsToAdd = []
      const itemsToKeep = []
      const sliceIndex = selectedItemsBar?.length - (newTotal - limit)

      itemsToAdd.push(...selectedItemsBar.slice(0, sliceIndex))
      itemsToKeep.push(...selectedItemsBar.slice(sliceIndex))

      if (itemsToKeep?.length) {
        const toastId = `${TOLGEE_PREFIX}-itemsToKeep-${Math.random()}`
        const folderId = foldersIds?.[0]

        createToast({
          id: toastId,
          timeout: 7_000,
          content: (
            <LinkToast
              toast={{
                message: t({
                  key: getTolgeeKey(limitReachedToastKey),
                  parameters: {
                    b: <strong></strong>,
                    p: <p></p>,
                  },
                  defaultValue:
                    '<p>Seu dashboard está <b>cheio</b>, para salvar com sucesso novas referências edite seu dashboard ou crie um novo.</p>',
                }),
                link: folderId
                  ? `/workspace/${workspaceId}/jobs/${selectedJob.id}/folder/${folderId}/all`
                  : `/workspace/${workspaceId}/jobs/${selectedJob.id}/folder/allReferences/all`,
                linkLabel: t({
                  key: getTolgeeKey(editJobToastLinkKey),
                  defaultValue: 'Edit dashboard',
                }),
                linkCallback: () => {
                  handleClose()
                  deleteToastById(toastId)
                },
              }}
            />
          ),
        })
      }

      if (itemsToAdd?.length) {
        const getType = item => {
          if (item?.type === 'savedPostSearches') {
            return 'saved_searches'
          }
          if (item?.type === 'industry_topic') {
            return 'industry_topics'
          }
          if (item?.type === 'global_topic') {
            return 'global_topics'
          }
          return item?.type
        }

        const inputVariables = itemsToAdd.map(item => ({
          provider: item?.provider ?? null,
          type: getType(item),
          entityId: String(item?.id),
          folders: foldersIds,
        }))

        const cleanInputVariables = inputVariables.filter(item => item.entityId)

        await savePinsToJobFolders({
          variables: {
            createWorkspaceJobPinInput: cleanInputVariables,
            workspaceJobId: jobId,
          },
          onCompleted: (...params) => {
            handleClose()
            setSelectedItemsBar(itemsToKeep)
            handleMutationCompleted(...params)
          },
          update: cache => {
            const formattedData = formatDataForCacheUpdate(cleanInputVariables)
            const cacheId = `WorkspaceJob:${jobId}`

            //currently we can only add items to one folder at a time
            //so we don't need to check all IDs
            const folderId = foldersIds?.[0]
            if (folderId) {
              cache.modify({
                id: `WorkspaceJobFolder:${folderId}`,
                fields: {
                  updatedAt: () => {
                    return new Date().toISOString()
                  },
                  pins: pinsAmount => {
                    const pinsSum = {
                      creatorAmount: Number(pinsAmount?.creatorAmount ?? 0) + Number(formattedData?.creatorAmount ?? 0),
                      postAmount: Number(pinsAmount?.postAmount ?? 0) + Number(formattedData?.postAmount ?? 0),
                      territoriesAmount:
                        Number(pinsAmount?.territoriesAmount ?? 0) + Number(formattedData?.territoriesAmount ?? 0),
                      totalPins: Number(pinsAmount?.totalPins ?? 0) + Number(formattedData?.totalPins ?? 0),
                    }
                    return {
                      ...pinsAmount,
                      ...pinsSum,
                    }
                  },
                },
              })
            }

            cache.modify({
              id: cacheId,
              fields: {
                updatedAt: () => {
                  return new Date().toISOString()
                },
                pins: pinsAmount => {
                  const pinsSum = {
                    creatorAmount: Number(pinsAmount?.creatorAmount ?? 0) + Number(formattedData?.creatorAmount ?? 0),
                    postAmount: Number(pinsAmount?.postAmount ?? 0) + Number(formattedData?.postAmount ?? 0),
                    territoriesAmount:
                      Number(pinsAmount?.territoriesAmount ?? 0) + Number(formattedData?.territoriesAmount ?? 0),
                    totalPins: Number(pinsAmount?.totalPins ?? 0) + Number(formattedData?.totalPins ?? 0),
                  }
                  return {
                    ...pinsAmount,
                    ...pinsSum,
                  }
                },
              },
            })
          },
        })
      }

      trackAddedItems({ jobId, items: itemsToAdd })
    },
    [
      jobsList,
      workspaceCompanyPlan,
      selectedItemsBar,
      createToast,
      t,
      getTolgeeKey,
      workspaceId,
      handleClose,
      deleteToastById,
      savePinsToJobFolders,
      setSelectedItemsBar,
      handleMutationCompleted,
    ]
  )

  const maxJobs = workspaceCompanyPlan?.jobs
  const foldersLimit = workspaceCompanyPlan?.jobFolders
  const hasReachedJobLimit = jobsRows >= maxJobs

  return (
    <Modal isOpen={isOpen} style={MODAL_STYLE} onRequestClose={handleClose} shouldCloseOnOverlayClick>
      <AddToJobFolderModalContent
        jobsList={jobsList}
        onClose={handleClose}
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        onSave={handleSavePinsToJobFolders}
        onClickNewJob={handleOpenDashboardCreationModal}
        setIsOpen={setIsOpen}
        disableJobCreation={hasReachedJobLimit}
        jobsCount={jobsRows}
        loadMoreJobs={loadMoreJobs}
        hasMoreJobs={hasMoreJobs}
        jobsLimit={maxJobs}
        foldersLimit={foldersLimit}
        mostRecentJob={mostRecentJob}
        isLoading={isLoading}
        isFetchingMore={isFetchingMore}
      />
    </Modal>
  )
}
